import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { vetrineContentService } from '../../../services/vetrineContentService';
import { format } from 'date-fns';

const ReviewsManager = ({ storeId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadReviews();
  }, [storeId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await vetrineContentService?.getAllReviewsByStore(storeId);
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await vetrineContentService?.approveReview(reviewId);
      await loadReviews();
    } catch (error) {
      console.error('Error approving review:', error);
      alert('Errore nell\'approvare la recensione: ' + error?.message);
    }
  };

  const handleReject = async (reviewId) => {
    if (!confirm('Sei sicuro di voler rifiutare questa recensione?')) return;
    
    try {
      await vetrineContentService?.rejectReview(reviewId);
      await loadReviews();
    } catch (error) {
      console.error('Error rejecting review:', error);
      alert('Errore nel rifiutare la recensione: ' + error?.message);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!confirm('Sei sicuro di voler eliminare questa recensione?')) return;
    
    try {
      await vetrineContentService?.deleteReview(reviewId);
      await loadReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Errore nell\'eliminare la recensione: ' + error?.message);
    }
  };

  const getStatoBadge = (stato) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'In Attesa' },
      approvata: { color: 'bg-green-100 text-green-700', label: 'Approvata' },
      rifiutata: { color: 'bg-red-100 text-red-700', label: 'Rifiutata' }
    };
    return badges?.[stato] || badges?.pending;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const filteredReviews = reviews?.filter((review) => {
    if (filter === 'all') return true;
    return review?.stato === filter;
  });

  if (loading) {
    return <div className="text-center py-8">Caricamento recensioni...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Recensioni ({reviews?.length})</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-smooth ${
              filter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Tutte ({reviews?.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg transition-smooth ${
              filter === 'pending' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            In Attesa ({reviews?.filter(r => r?.stato === 'pending')?.length})
          </button>
          <button
            onClick={() => setFilter('approvata')}
            className={`px-4 py-2 rounded-lg transition-smooth ${
              filter === 'approvata' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Approvate ({reviews?.filter(r => r?.stato === 'approvata')?.length})
          </button>
        </div>
      </div>

      {filteredReviews?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Star" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessuna recensione {filter !== 'all' && `${filter === 'pending' ? 'in attesa' : 'approvata'}`}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews?.map((review) => {
            const statoBadge = getStatoBadge(review?.stato);
            return (
              <div key={review?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{review?.nomeUtente}</h3>
                        <div className="flex items-center gap-2">
                          {renderStars(review?.rating)}
                          <span className="text-xs text-muted-foreground">
                            {review?.createdAt && format(new Date(review?.createdAt), 'dd/MM/yyyy HH:mm')}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${statoBadge?.color}`}>
                        {statoBadge?.label}
                      </span>
                      {review?.verificata && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                          <Icon name="CheckCircle" size={12} />
                          Verificata
                        </span>
                      )}
                    </div>
                    {review?.titolo && (
                      <h4 className="font-semibold mb-2">{review?.titolo}</h4>
                    )}
                    <p className="text-sm text-muted-foreground mb-3">{review?.commento}</p>
                    {review?.rispostaNegozio && (
                      <div className="bg-muted/50 rounded-lg p-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="MessageCircle" size={16} className="text-primary" />
                          <span className="text-sm font-semibold">Risposta del negozio</span>
                          {review?.rispostaData && (
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(review?.rispostaData), 'dd/MM/yyyy')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm">{review?.rispostaNegozio}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    {review?.stato === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(review?.id)}
                          className="p-2 hover:bg-green-100 text-green-700 rounded-lg transition-smooth"
                          title="Approva"
                        >
                          <Icon name="Check" size={16} />
                        </button>
                        <button
                          onClick={() => handleReject(review?.id)}
                          className="p-2 hover:bg-red-100 text-red-700 rounded-lg transition-smooth"
                          title="Rifiuta"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(review?.id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-smooth"
                      title="Elimina"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewsManager;